import { ConfigProvider, Layout, Menu, theme } from "antd";
import { useUserContext } from "../oidc";
import { useCallback, useMemo } from "react";
import { LogoutOutlined } from "@ant-design/icons";
import { Link, Outlet, useNavigate } from "react-router-dom";
import styled from "styled-components";
//import logo from '../../logo1.svg'
import logo from '../../plamo.jpg'
import { useAppDispatch, useAppSelector } from "../../store";
import { setSiderExpanded } from "../../ui-state-slice";
import { IconBuildingSolid, IconCashRegisterSolid, IconCircleUserSolid, IconEuroSignSolid, IconFileSignatureSolid, IconGearSolid, IconHouseSolid, IconListCheckSolidFull, IconListSolid, IconMapLocationDotSolid, IconPeopleGroupSolid, IconTableSolidFull } from "../../icon";
import SearchBar from "./search-bar";
import { useGetCustomersQuery, useGetEmployeesQuery } from "../../api/plamo-api";
import locale from 'antd/locale/de_DE';

/*
import dayjs from 'dayjs';

import 'dayjs/locale/de';

dayjs.locale('de', {
  weekStart: 1,
});
*/

const { Header, Content, Sider } = Layout


const useSiderExpanded = ():boolean => {
  return useAppSelector((state) => state.uiState.siderExpanded === undefined ? true : state.uiState.siderExpanded)
}

const useSetSiderExpanded = (): ((expanded:boolean) => void) => {
  const dispatch = useAppDispatch()
  return (expanded:boolean) => {
    dispatch(setSiderExpanded({ expanded }))
  }
}

const useSiderExpandedReduxState = (): [boolean, (expanded:boolean) => void] => {
  return [useSiderExpanded(), useSetSiderExpanded()]
}



const MenuStyleWrapper = styled.div`
  overflow: auto;
  height: 100%;
`

const SideMenu: React.FC = () => {  
  const menuItems = useMemo(() => ([
    {
      key: 'customer',
      icon: <IconHouseSolid />,
      label: <Link to={`/customer`}>Kunden</Link>
    },
    {
      key: 'employee',
      icon: <IconCircleUserSolid />,
      label: <Link to={`/employee`}>Mitarbeitende</Link>
    },
    {
      key: 'tasks',
      icon: <IconListCheckSolidFull />,
      label: <Link to="/task">Todos</Link>
    },
    /*
    {
      key: 'assignment',
      icon: <SwapOutlined />,
      label: <Link to="/assignment">Zuordnungen</Link>
    },
    */
    {
      key: 'map',
      icon: <IconMapLocationDotSolid />,
      label: <Link to="/map">Karte</Link>
    },
    {
      key: 'settlement',
      icon: <IconCashRegisterSolid />,
      label: <Link to="/settlement">Abrechnung</Link>
    },
    {
      key: 'reports',
      icon: <IconTableSolidFull />,
      label: <Link to="/reports">Auswertungen</Link>
    },
    {
      key: 'settings',
      icon: <IconGearSolid />,
      label: "Einstellungen",
      children: [
        {
          key: "general",
          icon: <IconGearSolid />,
          label: <Link to="/general-settings">Allgemein</Link>
        },
        {
          key: "teams",
          icon: <IconPeopleGroupSolid />,
          label: <Link to="/team">Teams</Link>
        },
        {
          key: "enums",
          icon: <IconListSolid />,
          label: <Link to="/enums">Aufzählungslisten</Link>
        },
        {
          key: "insurances",
          icon: <IconBuildingSolid />,
          label: <Link to="/insurance">Versicherungen</Link>
        },
        {
          key: "locations",
          icon: <IconBuildingSolid />,
          label: <Link to="/location">Standorte</Link>
        },
        {
          key: 'contract-templates',
          icon: <IconFileSignatureSolid />,
          label: <Link to="/contract-template">Vertragsvorlagen</Link>
        },
        {
          key: 'contract-settings',
          icon: <IconFileSignatureSolid />,
          label: <Link to="/contract-settings">Vertragseinstellungen</Link>
        },
        {
          key: 'settings-map',
          icon: <IconMapLocationDotSolid />,
          label: <Link to="/settings-map">Karteneinstellungen</Link>
        },
        {
          key: 'price-settings',
          icon: <IconEuroSignSolid />,
          label: <Link to="/price-settings">Preisgestaltung</Link>
        },
        {
          key: 'default-tasks',
          icon: <IconListCheckSolidFull />,
          label: <Link to="/default-tasks">Todos</Link>   
        },
        {
          key: 'invoice-settings',
          icon: <IconListCheckSolidFull />,
          label: <Link to="/invoice-settings">Rechnungseinstellungen</Link>   
        },
      ]
    }
  ]), [])

  return (
    <>
      <MenuStyleWrapper>
        <Menu
          mode="inline"
          style={{ height: '100%', borderRight: 0}}
          items={menuItems}
          />
      </MenuStyleWrapper>
    </>
  )
}

const TopMenu = () => {
  const { user, logout } = useUserContext()

  const onClick = useCallback((item:any) => {
    if(item.key === "logout" && logout) {
      logout()
    }
  }, [logout])

  return (
    <Menu
      theme="light"
      mode="horizontal"
      defaultSelectedKeys={['2']}
      onClick={onClick}
      items={[
        {
          key: "user",
          icon: <IconCircleUserSolid />,
          label: user?.usernameDisplay,
          children: [
            {
              key: "logout",
              icon: <LogoutOutlined />,
              label: "Abmelden"
            }
          ]
        }
      ]}
    />
  )
}

function Root() {
  const [siderExpanded, setSiderExpanded] = useSiderExpandedReduxState()
  const navigate = useNavigate();

  const { data: employees } = useGetEmployeesQuery({}) as any;
  const { data: customers } = useGetCustomersQuery({}) as any;

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const onSearch = useCallback(async (searchObj) => {
      if(searchObj.type === 'employee') {
        navigate(`/employee/${searchObj.id}`)
      } else if(searchObj.type === 'customer') {
        navigate(`/customer/${searchObj.id}`)
      }
      
    }, [navigate]);

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        algorithm: theme.defaultAlgorithm,

        token: {
          //colorPrimary: '#2D5DF5',
          
          // Seed Token
          //colorPrimary: '#00b96b',
          //borderRadius: 2,

          // Alias Token
          //colorBgContainer: '#f6ffed',
        },
      }}
    >
      <Layout style={{ height: "100vh", overflow: "hidden" }}>
        <Header style={{ display: 'flex', backgroundColor: 'white' }}>
          <img src={logo} alt="" style={{ height: "3em", marginTop: "1em", cursor: 'pointer' }} onClick={() => navigate('/')}/>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', flexDirection: "row-reverse" }}>
            <SearchBar onSearch={onSearch} employees={employees?.items} customers={customers?.items} />
            <TopMenu />
          </div>
        </Header>
        <Layout>
          <Sider collapsible collapsed={!siderExpanded} onCollapse={collapsed => setSiderExpanded(!collapsed)} width={300} style={{ background: colorBgContainer }}>
            <SideMenu />
          </Sider>
          <Layout style={{ padding: 0, paddingLeft: '24px', paddingRight: '24px' }}>
            <Content
                  style={{
                    padding: 24,
                    paddingBottom: 0,
                    margin: 0,
                    minHeight: 280,
                    background: colorBgContainer,
                    height: "100%",
                    overflow: "auto"
                  }}>
                <Outlet />
              </Content>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  )
}

export default Root;